import { defaultTagId } from './chunk-AHISJLEK.js';
import { pricedAboveDust, claimedProtocols, FALLBACK_PROTOCOL_ID, BARE_TRANSFER_PROTOCOLS } from './chunk-O5TCTRWM.js';
import { requireForensicParam, forensicCheck, clampForensicParam } from './chunk-O3OIBNP7.js';

// src/poisoning/index.ts
var POISONING_CHECK_ID = "evm.address_poisoning";
var POLICY_PARAMS = Object.freeze({
  minSharedPrefix: "min_shared_prefix",
  minSharedSuffix: "min_shared_suffix"
});
var POISONING_POLICY_PARAMS = Object.freeze({
  minSharedPrefix: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.minSharedPrefix),
  minSharedSuffix: requireForensicParam(POISONING_CHECK_ID, POLICY_PARAMS.minSharedSuffix)
});
var DEFAULT_POISONING_POLICY = Object.freeze({
  minSharedPrefix: POISONING_POLICY_PARAMS.minSharedPrefix.default,
  minSharedSuffix: POISONING_POLICY_PARAMS.minSharedSuffix.default
});
function poisoningPolicyFromParams(params) {
  const read = (field) => {
    const value = params?.[POLICY_PARAMS[field]];
    return typeof value === "number" && Number.isFinite(value) ? value : DEFAULT_POISONING_POLICY[field];
  };
  return { minSharedPrefix: read("minSharedPrefix"), minSharedSuffix: read("minSharedSuffix") };
}
function clampPoisoningPolicy(policy) {
  return {
    minSharedPrefix: clampForensicParam(POISONING_POLICY_PARAMS.minSharedPrefix, policy.minSharedPrefix),
    minSharedSuffix: clampForensicParam(POISONING_POLICY_PARAMS.minSharedSuffix, policy.minSharedSuffix)
  };
}
function poisoningPolicyWithinLimits(policy) {
  return Object.keys(POLICY_PARAMS).every((field) => {
    const value = policy[field];
    return Number.isFinite(value) && clampPoisoningPolicy(policy)[field] === value;
  });
}
var POISONING_CATALOG_TAGS = Object.freeze(
  (forensicCheck(POISONING_CHECK_ID)?.tags ?? []).map((tag) => tag.name)
);
var POISONING_TAG_KEYS = new Set(
  POISONING_CATALOG_TAGS.flatMap((name) => [foldTag(name), defaultTagId(name)])
);
function foldTag(value) {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}
function isPoisoningCatalogTag(value) {
  const text = (value ?? "").trim();
  if (!text) return false;
  return POISONING_TAG_KEYS.has(text) || POISONING_TAG_KEYS.has(foldTag(text));
}
var EVM_ADDRESS = /^0x[0-9a-f]{40}$/;
function evmAddress(address) {
  const a = (address ?? "").toLowerCase();
  return EVM_ADDRESS.test(a) ? a : null;
}
var normalise = evmAddress;
function sharedAffixes(a, b) {
  const x = normalise(a);
  const y = normalise(b);
  if (!x || !y) return null;
  const bx = x.slice(2);
  const by = y.slice(2);
  let prefix = 0;
  while (prefix < bx.length && bx[prefix] === by[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < bx.length && bx[bx.length - 1 - suffix] === by[by.length - 1 - suffix]) suffix += 1;
  return { prefix, suffix };
}
function isLookalike(a, b, policy = DEFAULT_POISONING_POLICY) {
  const shared = sharedAffixes(a, b);
  if (!shared) return false;
  if (shared.prefix === 40) return false;
  return shared.prefix >= policy.minSharedPrefix && shared.suffix >= policy.minSharedSuffix;
}
function deriveImpersonations(rows, read, subjects, policy = DEFAULT_POISONING_POLICY, catalogued) {
  const subjectSet = /* @__PURE__ */ new Set();
  for (const s of subjects) {
    const a = normalise(s);
    if (a) subjectSet.add(a);
  }
  if (subjectSet.size === 0) return /* @__PURE__ */ new Map();
  const genuine = /* @__PURE__ */ new Set();
  const unprovenSenders = /* @__PURE__ */ new Set();
  const realSenders = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const candidate = read(row);
    const from = normalise(candidate.fromAddress);
    const to = normalise(candidate.toAddress);
    if (!from || !to || from === to) continue;
    if (subjectSet.has(from) && !subjectSet.has(to)) {
      if (!catalogued?.has(to)) genuine.add(to);
      continue;
    }
    if (subjectSet.has(to) && !subjectSet.has(from)) {
      if (catalogued?.has(from)) {
        unprovenSenders.add(from);
      } else if (pricedAboveDust(candidate.valueUsd) || candidate.provenValue === true) {
        genuine.add(from);
        realSenders.add(from);
      } else {
        unprovenSenders.add(from);
      }
    }
  }
  const out = /* @__PURE__ */ new Map();
  for (const sender of unprovenSenders) {
    if (realSenders.has(sender) || subjectSet.has(sender)) continue;
    let best = null;
    for (const target of genuine) {
      if (target === sender || !isLookalike(sender, target, policy)) continue;
      const shared = sharedAffixes(sender, target);
      if (!best || shared.prefix + shared.suffix > best.prefix + best.suffix) {
        best = { sender, mimics: target, ...shared };
      }
    }
    if (best) out.set(sender, best);
  }
  return out;
}
function short(address, prefix, suffix) {
  const head = Math.max(4, prefix);
  const tail = Math.max(4, suffix);
  return `0x${address.slice(2, 2 + head)}\u2026${address.slice(-tail)}`;
}
function revealing(address, prefix, suffix) {
  const body = address.slice(2);
  const head = Math.max(4, prefix + 2);
  const tail = Math.max(4, suffix + 2);
  if (head + tail >= body.length) return address;
  return `0x${body.slice(0, head)}\u2026${body.slice(body.length - tail)}`;
}
function namesNothingBeyondATransfer(row) {
  const claimed = claimedProtocols(row);
  return claimed.length > 0 && claimed.every((id) => id === FALLBACK_PROTOCOL_ID || BARE_TRANSFER_PROTOCOLS.has(id));
}
function poisoningReason(row, impersonations, catalogued) {
  const sender = normalise(row.fromAddress);
  if (!sender) return null;
  const hit = impersonations.get(sender);
  const tagged = catalogued?.has(sender) === true;
  if (!hit && !tagged) return null;
  if (pricedAboveDust(row.valueUsd)) return null;
  if (row.provenValue === true) return null;
  if (!namesNothingBeyondATransfer(row)) return null;
  if (!hit) {
    return `Sent from ${sender}, which the catalog tags as an address-poisoning lookalike (${CATALOGUED_BY}). Address poisoning: the transfer plants the lookalike in this history so a later copy-paste pays it \u2014 compare any address copied from here character by character against the one you mean to pay.`;
  }
  const look = revealing(hit.sender, hit.prefix, hit.suffix);
  const real = revealing(hit.mimics, hit.prefix, hit.suffix);
  const disguise = short(hit.sender, hit.prefix, hit.suffix);
  return `Sent from ${look}, a lookalike of ${real} \u2014 an address this history genuinely transacted with. Both display as ${disguise} (${hit.prefix} leading and ${hit.suffix} trailing characters match), which is what the attack relies on. Address poisoning: the transfer plants the lookalike so a later copy-paste pays it.` + (tagged ? ` The catalog tags the sender as an address-poisoning lookalike too.` : "");
}
var CATALOGUED_BY = "its own activity across other wallets, or an explorer's report";
function paidLookalikeReason(row, impersonations, subjects, catalogued) {
  const from = normalise(row.fromAddress);
  const to = normalise(row.toAddress);
  if (!from || !to || !subjects.has(from)) return null;
  const hit = impersonations.get(to);
  if (!hit && catalogued?.has(to) !== true) return null;
  if (!pricedAboveDust(row.valueUsd) && row.provenValue !== true) return null;
  if (!hit) {
    return `Paid ${to}, which the catalog tags as an address-poisoning lookalike (${CATALOGUED_BY}). This is what address poisoning plants the lookalike for: the funds went to the impersonator, not the payee it imitates.`;
  }
  const look = revealing(hit.sender, hit.prefix, hit.suffix);
  const real = revealing(hit.mimics, hit.prefix, hit.suffix);
  const disguise = short(hit.sender, hit.prefix, hit.suffix);
  return `Paid ${look}, a lookalike of ${real} \u2014 the address this history genuinely paid. Both display as ${disguise}. This is what address poisoning plants the lookalike for: the funds went to the impersonator, not the intended payee.`;
}
var SPOOF_BATCH_MIN_SENDERS = 3;
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var TOKEN_KINDS = /* @__PURE__ */ new Set(["erc20", "nft"]);
function isZero(raw) {
  const text = (raw ?? "").trim();
  if (!/^(0x[0-9a-f]+|\d+)$/i.test(text)) return false;
  try {
    return BigInt(text) === 0n;
  } catch {
    return false;
  }
}
function fabricatesSender(leg, called) {
  return writesTheCalledContractsLedger(leg, called) || movesNothing(leg, called);
}
function writesTheCalledContractsLedger(leg, called) {
  const target = normalise(called);
  const from = normalise(leg.from);
  if (target === null || !from || from === target) return false;
  return normalise(leg.token) === target;
}
function movesNothing(leg, called) {
  const from = normalise(leg.from);
  if (!from || from === normalise(called)) return false;
  return isZero(leg.value);
}
function spoofedTransfers(input) {
  const signer = normalise(input.signer);
  const called = normalise(input.called);
  const claimed = [];
  for (const t of input.transfers) {
    if (!TOKEN_KINDS.has(String(t.kind ?? ""))) continue;
    const from = normalise(t.from);
    const to = normalise(t.to);
    if (!from || from === ZERO_ADDRESS || from === signer) continue;
    claimed.push({ ...t, from, to, token: normalise(t.token) });
  }
  const count = (legs) => ({
    senders: new Set(legs.map((l) => l.from)).size,
    recipients: new Set(legs.map((l) => l.to).filter(Boolean)).size
  });
  const emittedByCalled = claimed.filter((l) => writesTheCalledContractsLedger(l, called));
  const batch = count(emittedByCalled);
  if (batch.senders >= SPOOF_BATCH_MIN_SENDERS) {
    return {
      kind: "batch",
      legs: emittedByCalled,
      ...batch,
      reason: `The contract this transaction called logged ${emittedByCalled.length} transfers of its own token claiming to come from ${batch.senders} different wallets, none of which signed it. A token only moves a holder's balance on that holder's signature or approval, so these entries are fabricated \u2014 the way an address-poisoning campaign plants lookalike senders in ${batch.recipients} wallet${batch.recipients === 1 ? "'s" : "s'"} histories at once. Do not copy an address from these transfers.`
    };
  }
  const zero = claimed.filter((l) => movesNothing(l, called));
  if (zero.length > 0) {
    const z = count(zero);
    return {
      kind: "zero_value",
      legs: zero,
      ...z,
      reason: `${zero.length} zero-value transfer${zero.length === 1 ? "" : "s"} name${zero.length === 1 ? "s" : ""} a sender that did not sign this transaction. Moving nothing needs no approval, which is why address poisoning uses it: the recipient's history gains an entry that looks like a payment from a lookalike. Do not copy an address from ${zero.length === 1 ? "it" : "them"}.`
    };
  }
  return null;
}

export { DEFAULT_POISONING_POLICY, POISONING_CATALOG_TAGS, POISONING_CHECK_ID, POISONING_POLICY_PARAMS, SPOOF_BATCH_MIN_SENDERS, clampPoisoningPolicy, deriveImpersonations, evmAddress, fabricatesSender, isLookalike, isPoisoningCatalogTag, paidLookalikeReason, poisoningPolicyFromParams, poisoningPolicyWithinLimits, poisoningReason, sharedAffixes, spoofedTransfers };
//# sourceMappingURL=chunk-LD6PO66H.js.map
//# sourceMappingURL=chunk-LD6PO66H.js.map